import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Trophy, Users, Gamepad2, ChevronRight, ChevronLeft, Check,
  Dice5, Spade, Dices, Layers, Grid3X3, Settings2, Search,
  UserPlus, Link2, X, Loader2, Swords, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import api from "../lib/api";

const CATEGORY_ICONS = {
  okey: Dice5,
  batak: Spade,
  tavla: Dices,
  iskambil: Layers,
  strateji: Grid3X3,
  sosyal: Users
};

const STEPS = [
  { id: 1, title: "Organizasyon", icon: Trophy, description: "Lig mi turnuva mı?" },
  { id: 2, title: "Oyun", icon: Gamepad2, description: "Hangi oyunu oynayacaksınız?" },
  { id: 3, title: "Kurallar", icon: Settings2, description: "Oyun kurallarını ayarlayın" },
  { id: 4, title: "Katılımcılar", icon: Users, description: "Oyuncuları davet edin" },
  { id: 5, title: "Detaylar", icon: Check, description: "Son ayarlar" }
];

export default function LeagueWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Organization Type
  const [orgType, setOrgType] = useState("league"); // league or tournament
  
  // Step 2: Game Selection
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedGame, setSelectedGame] = useState("");
  
  // Step 3: Rules
  const [customRules, setCustomRules] = useState({});
  
  // Step 4: Participants
  const [playerUsernames, setPlayerUsernames] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Step 5: Details
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [weeklyGames, setWeeklyGames] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gamesRes, catsRes] = await Promise.all([
          api.getGames(),
          api.getGameCategories()
        ]);
        setGames(gamesRes.data);
        setCategories(catsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const selectedGameData = games.find(g => g.game_id === selectedGame);
  
  const gamesByCategory = categories.reduce((acc, cat) => {
    acc[cat.id] = games.filter(g => g.category === cat.id);
    return acc;
  }, {});

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await api.searchUsers(query);
      setSearchResults(response.data.filter(u => !playerUsernames.includes(u.username)));
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  const addPlayer = (user) => {
    if (!playerUsernames.includes(user.username)) {
      setPlayerUsernames([...playerUsernames, user.username]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const removePlayer = (username) => {
    setPlayerUsernames(playerUsernames.filter(u => u !== username));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return orgType;
      case 2: return selectedGame;
      case 3: return true; // Rules are optional
      case 4: return playerUsernames.length >= 2;
      case 5: return name.trim().length > 0;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    
    setLoading(true);
    try {
      if (orgType === "league") {
        const response = await api.createLeague({
          name,
          game_id: selectedGame,
          description,
          player_usernames: playerUsernames,
          is_private: isPrivate,
          custom_rules: customRules,
          weekly_games: weeklyGames
        });
        toast.success("Lig oluşturuldu!");
        navigate(`/league/${response.data.league_id}`);
      } else {
        const response = await api.createTournament({
          name,
          game_id: selectedGame,
          description,
          player_usernames: playerUsernames,
          bracket_type: "single_elimination"
        });
        toast.success("Turnuva oluşturuldu!");
        navigate(`/tournament/${response.data.tournament_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (canProceed() && currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="league-wizard">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div 
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isActive ? "bg-[#4DFFFF] text-[#0B1622]" :
                      isCompleted ? "bg-green-500 text-white" :
                      "bg-white/10 text-[#64748B]"
                    }`}
                  >
                    {isCompleted ? <Check className="w-6 h-6" /> : <StepIcon className="w-6 h-6" />}
                  </div>
                  <p className={`text-xs mt-2 font-medium ${isActive ? "text-[#4DFFFF]" : "text-[#64748B]"}`}>
                    {step.title}
                  </p>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-12 md:w-24 h-1 mx-2 rounded ${
                    isCompleted ? "bg-green-500" : "bg-white/10"
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="glass-card p-6 md:p-8 min-h-[400px]">
        {/* Step 1: Organization Type */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Ne oluşturmak istiyorsun?</h2>
              <p className="text-[#B1C1C1] mt-2">Organizasyon türünü seç</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <button
                type="button"
                onClick={() => setOrgType("league")}
                className={`p-8 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                  orgType === "league" 
                    ? "border-[#4DFFFF] bg-[#4DFFFF]/10" 
                    : "border-white/10 hover:border-white/30"
                }`}
                data-testid="select-league"
              >
                <Trophy className="w-12 h-12 text-[#C6A87C] mb-4" />
                <h3 className="text-xl font-bold text-white">Lig</h3>
                <p className="text-[#B1C1C1] mt-2">
                  Haftalık maçlar, puan tablosu, sezon sonu şampiyonu belirlenir.
                </p>
                <ul className="mt-4 space-y-1 text-sm text-[#64748B]">
                  <li>• Haftalık sonuç girişi</li>
                  <li>• Puan sıralaması</li>
                  <li>• AI yorumları</li>
                </ul>
              </button>
              
              <button
                type="button"
                onClick={() => setOrgType("tournament")}
                className={`p-8 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                  orgType === "tournament" 
                    ? "border-[#4DFFFF] bg-[#4DFFFF]/10" 
                    : "border-white/10 hover:border-white/30"
                }`}
                data-testid="select-tournament"
              >
                <Swords className="w-12 h-12 text-[#F61225] mb-4" />
                <h3 className="text-xl font-bold text-white">Turnuva</h3>
                <p className="text-[#B1C1C1] mt-2">
                  Eleme usulü bracket sistemi. Kaybeden elenir, şampiyon belirlenir.
                </p>
                <ul className="mt-4 space-y-1 text-sm text-[#64748B]">
                  <li>• Tek eleme bracket</li>
                  <li>• Hızlı sonuç</li>
                  <li>• Görsel bracket</li>
                </ul>
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Game Selection */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white">Oyun Seç</h2>
              <p className="text-[#B1C1C1] mt-2">Hangi oyunu oynayacaksınız?</p>
            </div>
            
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {categories.map((category) => {
                const CategoryIcon = CATEGORY_ICONS[category.id] || Gamepad2;
                const categoryGames = gamesByCategory[category.id] || [];
                const isExpanded = expandedCategory === category.id;
                
                return (
                  <div key={category.id} className="border border-white/10 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${category.color}20` }}
                        >
                          <CategoryIcon className="w-5 h-5" style={{ color: category.color }} />
                        </div>
                        <div className="text-left">
                          <p className="text-white font-medium">{category.name}</p>
                          <p className="text-xs text-[#64748B]">{categoryGames.length} oyun</p>
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" />}
                    </button>
                    
                    {isExpanded && (
                      <div className="p-4 pt-0 grid grid-cols-2 md:grid-cols-3 gap-2">
                        {categoryGames.map((game) => (
                          <button
                            key={game.game_id}
                            type="button"
                            onClick={() => setSelectedGame(game.game_id)}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedGame === game.game_id
                                ? "border-[#4DFFFF] bg-[#4DFFFF]/10"
                                : "border-white/10 hover:border-white/30"
                            }`}
                            data-testid={`select-game-${game.game_id}`}
                          >
                            <p className="text-sm text-white font-medium truncate">{game.name_tr}</p>
                            <p className="text-xs text-[#64748B]">{game.min_players}-{game.max_players} kişi</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {selectedGameData && (
              <div className="p-4 bg-[#4DFFFF]/10 border border-[#4DFFFF]/30 rounded-lg">
                <p className="text-[#4DFFFF] font-medium">Seçilen: {selectedGameData.name_tr}</p>
                <p className="text-sm text-[#B1C1C1] mt-1">{selectedGameData.description}</p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Rules */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white">Kural Ayarları</h2>
              <p className="text-[#B1C1C1] mt-2">
                {selectedGameData?.name_tr} için kuralları özelleştirin
              </p>
            </div>
            
            {/* Online Oyun Modu - 101 Okey için */}
            {selectedGameData?.supports_online && (
              <div className="mb-6 p-4 bg-gradient-to-r from-[#C6A87C]/20 to-[#4DFFFF]/10 border border-[#C6A87C]/30 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C6A87C]/20 flex items-center justify-center">
                    <Gamepad2 className="w-5 h-5 text-[#C6A87C]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Online Oyun Desteği</h3>
                    <p className="text-xs text-[#B1C1C1]">Bu oyun online olarak oynanabilir!</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCustomRules({...customRules, enable_online: !customRules.enable_online})}
                    className={`ml-auto px-4 py-2 rounded-lg font-medium transition-all ${
                      customRules.enable_online 
                        ? "bg-[#4DFFFF] text-[#0B1622]" 
                        : "bg-white/10 text-[#B1C1C1] hover:bg-white/20"
                    }`}
                  >
                    {customRules.enable_online ? "✓ Online Aktif" : "Online Etkinleştir"}
                  </button>
                </div>
                
                {customRules.enable_online && selectedGameData?.online_rules && (
                  <div className="space-y-4 pt-4 border-t border-white/10">
                    <h4 className="text-[#C6A87C] font-semibold text-sm uppercase tracking-wider">Online Oyun Kuralları</h4>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedGameData.online_rules.map((rule) => (
                        <div key={rule.name} className="p-4 bg-[#050E2E]/50 rounded-lg border border-white/5">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <Label className="text-white font-medium">{rule.label}</Label>
                              {rule.description && (
                                <p className="text-xs text-[#64748B] mt-1">{rule.description}</p>
                              )}
                            </div>
                          </div>
                          
                          {rule.type === "select" ? (
                            <select
                              value={customRules[rule.name] ?? rule.default}
                              onChange={(e) => {
                                const val = rule.options.every(o => typeof o === 'number') 
                                  ? Number(e.target.value) 
                                  : e.target.value;
                                setCustomRules({...customRules, [rule.name]: val});
                              }}
                              className="w-full mt-3 p-3 bg-[#0B1622] border border-white/10 rounded-lg text-white"
                            >
                              {rule.options.map(opt => (
                                <option key={opt} value={opt}>
                                  {typeof opt === 'number' ? `${opt} saniye` : opt}
                                </option>
                              ))}
                            </select>
                          ) : rule.type === "boolean" ? (
                            <button
                              type="button"
                              onClick={() => setCustomRules({...customRules, [rule.name]: !(customRules[rule.name] ?? rule.default)})}
                              className={`mt-3 w-full p-3 rounded-lg border transition-all flex items-center justify-between ${
                                (customRules[rule.name] ?? rule.default) 
                                  ? "bg-[#4DFFFF]/20 border-[#4DFFFF]/50 text-[#4DFFFF]" 
                                  : "bg-white/5 border-white/10 text-[#64748B]"
                              }`}
                            >
                              <span>{(customRules[rule.name] ?? rule.default) ? "Aktif" : "Pasif"}</span>
                              <div className={`w-10 h-6 rounded-full transition-colors relative ${
                                (customRules[rule.name] ?? rule.default) ? "bg-[#4DFFFF]" : "bg-white/20"
                              }`}>
                                <div 
                                  className="w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all duration-200"
                                  style={{
                                    left: (customRules[rule.name] ?? rule.default) ? '18px' : '2px'
                                  }}
                                />
                              </div>
                            </button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Normal Kurallar */}
            {selectedGameData?.customizable_rules?.length > 0 ? (
              <div>
                <h4 className="text-[#B1C1C1] font-semibold text-sm uppercase tracking-wider mb-4">Genel Kurallar</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedGameData.customizable_rules.map((rule) => (
                    <div key={rule.name} className="p-4 bg-white/5 rounded-lg">
                      <Label className="text-[#B1C1C1] text-sm">{rule.label}</Label>
                      {rule.type === "select" ? (
                        <select
                          value={customRules[rule.name] ?? rule.default}
                          onChange={(e) => setCustomRules({...customRules, [rule.name]: e.target.value})}
                          className="w-full mt-2 p-3 bg-[#050E2E] border border-white/10 rounded-lg text-white"
                        >
                          {rule.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : rule.type === "boolean" ? (
                        <div className="flex items-center gap-3 mt-2">
                          <button
                            type="button"
                            onClick={() => setCustomRules({...customRules, [rule.name]: !(customRules[rule.name] ?? rule.default)})}
                            className={`w-14 h-8 rounded-full transition-colors ${
                              (customRules[rule.name] ?? rule.default) ? "bg-[#4DFFFF]" : "bg-white/20"
                            }`}
                          >
                            <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                              (customRules[rule.name] ?? rule.default) ? "translate-x-7" : "translate-x-1"
                            }`} />
                          </button>
                          <span className="text-sm text-white">
                            {(customRules[rule.name] ?? rule.default) ? "Açık" : "Kapalı"}
                          </span>
                        </div>
                      ) : (
                        <Input
                          type="number"
                          value={customRules[rule.name] ?? rule.default}
                          onChange={(e) => setCustomRules({...customRules, [rule.name]: Number(e.target.value)})}
                          min={rule.min}
                          max={rule.max}
                          className="mt-2 bg-[#050E2E] border-white/10 text-white h-12"
                        />
                      )}
                      {rule.min !== undefined && (
                        <p className="text-xs text-[#64748B] mt-1">
                          Min: {rule.min}, Max: {rule.max}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : !selectedGameData?.supports_online && (
              <div className="text-center py-12">
                <Settings2 className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
                <p className="text-[#B1C1C1]">Bu oyun için özelleştirilebilir kural bulunmuyor.</p>
                <p className="text-sm text-[#64748B] mt-2">Varsayılan kurallar uygulanacak.</p>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Participants */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white">Katılımcılar</h2>
              <p className="text-[#B1C1C1] mt-2">
                {selectedGameData ? `${selectedGameData.min_players}-${selectedGameData.max_players} oyuncu ekleyebilirsiniz` : "Oyuncuları davet edin"}
              </p>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Kullanıcı adı ara..."
                className="pl-12 bg-[#050E2E] border-white/10 text-white h-14"
              />
              {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4DFFFF] animate-spin" />}
              
              {searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#0B1622] border border-white/10 rounded-lg shadow-xl z-10 max-h-60 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button
                      key={user.user_id}
                      type="button"
                      onClick={() => addPlayer(user)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors"
                    >
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#4DFFFF]/20 flex items-center justify-center text-[#4DFFFF] font-bold">
                          {user.name?.charAt(0)}
                        </div>
                      )}
                      <div className="text-left">
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-sm text-[#64748B]">@{user.username}</p>
                      </div>
                      <UserPlus className="w-5 h-5 text-[#4DFFFF] ml-auto" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Added Players */}
            <div className="space-y-2">
              <Label className="text-[#C6A87C]">Eklenen Oyuncular ({playerUsernames.length})</Label>
              {playerUsernames.length === 0 ? (
                <p className="text-[#64748B] text-sm py-4 text-center">Henüz oyuncu eklenmedi</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {playerUsernames.map((username) => (
                    <div 
                      key={username}
                      className="flex items-center gap-2 px-4 py-2 bg-[#4DFFFF]/10 border border-[#4DFFFF]/30 rounded-full"
                    >
                      <span className="text-white">@{username}</span>
                      <button
                        type="button"
                        onClick={() => removePlayer(username)}
                        className="text-[#64748B] hover:text-red-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Invite Link */}
            <div className="p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Link2 className="w-5 h-5 text-[#C6A87C]" />
                <div>
                  <p className="text-white font-medium">Davet Linki</p>
                  <p className="text-sm text-[#64748B]">Lig oluşturulduktan sonra davet linki paylaşabilirsiniz</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Details */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white">Son Detaylar</h2>
              <p className="text-[#B1C1C1] mt-2">
                {orgType === "league" ? "Lig" : "Turnuva"} bilgilerini tamamlayın
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-[#B1C1C1]">{orgType === "league" ? "Lig" : "Turnuva"} Adı *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Örn: Kahve Köşesi Ligi"
                  className="mt-2 bg-[#050E2E] border-white/10 text-white h-14"
                  data-testid="league-name-input"
                />
              </div>
              
              <div>
                <Label className="text-[#B1C1C1]">Açıklama (Opsiyonel)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Lig/turnuva hakkında kısa bir açıklama..."
                  className="mt-2 bg-[#050E2E] border-white/10 text-white"
                  rows={3}
                />
              </div>
              
              {orgType === "league" && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#B1C1C1]">Haftalık Oyun Sayısı</Label>
                    <Input
                      type="number"
                      value={weeklyGames}
                      onChange={(e) => setWeeklyGames(Number(e.target.value))}
                      min={1}
                      max={10}
                      className="mt-2 bg-[#050E2E] border-white/10 text-white h-14"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-[#B1C1C1]">Gizlilik</Label>
                    <div className="flex items-center gap-3 mt-4">
                      <button
                        type="button"
                        onClick={() => setIsPrivate(!isPrivate)}
                        className={`w-14 h-8 rounded-full transition-colors ${
                          isPrivate ? "bg-[#4DFFFF]" : "bg-white/20"
                        }`}
                      >
                        <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                          isPrivate ? "translate-x-7" : "translate-x-1"
                        }`} />
                      </button>
                      <span className="text-white">{isPrivate ? "Özel Lig" : "Herkese Açık"}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Summary */}
            <div className="p-4 bg-white/5 rounded-lg space-y-2">
              <h3 className="text-[#C6A87C] font-medium mb-3">Özet</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-[#64748B]">Tür:</span>
                <span className="text-white">{orgType === "league" ? "Lig" : "Turnuva"}</span>
                <span className="text-[#64748B]">Oyun:</span>
                <span className="text-white">{selectedGameData?.name_tr}</span>
                <span className="text-[#64748B]">Oyuncular:</span>
                <span className="text-white">{playerUsernames.length} kişi</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button
          type="button"
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
          className="border-white/20 text-white hover:bg-white/10"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Geri
        </Button>
        
        {currentStep < 5 ? (
          <Button
            type="button"
            onClick={nextStep}
            disabled={!canProceed()}
            className="bg-[#4DFFFF] text-[#0B1622] hover:bg-[#4DFFFF]/80"
            data-testid="wizard-next-btn"
          >
            İleri
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !canProceed()}
            className="bg-gradient-to-r from-[#C6A87C] to-[#4DFFFF] text-[#0B1622] hover:opacity-90"
            data-testid="wizard-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Oluşturuluyor...
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                {orgType === "league" ? "Lig" : "Turnuva"} Oluştur
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
